import { useState } from 'react';
import { addToCart } from '../../../utils/cart';
import ProductReviews from '../../../components/feature/ProductReviews';

interface ProductQuickViewProps {
  product: typeof products[0];
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: typeof products[0]) => void;
}

export default function ProductQuickView({ product, isOpen, onClose, onAddToCart }: ProductQuickViewProps) {
  const [quantity, setQuantity] = useState(1);
  const [showReviews, setShowReviews] = useState(false);

  const handleAddToCart = () => {
    onAddToCart(product);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-900">Quick View</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Product Image */}
              <div className="relative w-full h-96 rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
                {product.recurring && (
                  <div className="absolute top-4 right-4 bg-accent-green text-white px-3 py-1 rounded-full text-xs font-medium">
                    {product.recurringNote}
                  </div>
                )}
                {product.price < 50 && (
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    🔥 HOT DEAL
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div>
                <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-3">
                  {product.category}
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {product.name}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {product.description}
                </p>

                {/* Price & Commission */}
                <div className="bg-gradient-to-br from-primary/5 to-accent-green/5 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Price</div>
                      <div className="text-3xl font-bold text-gray-900">
                        ${product.price}
                      </div>
                      {product.recurring && (
                        <div className="text-sm text-gray-500">
                          per {product.recurringNote?.toLowerCase()}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 mb-1">Your Commission</div>
                      <div className="text-2xl font-bold text-accent-green">
                        ${product.commission}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quantity Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 text-center border border-gray-300 rounded-lg py-2 font-semibold"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap"
                >
                  Add to Cart
                </button>

                {/* Features */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <i className="ri-shield-check-line text-accent-green text-lg"></i>
                    <span>Secure checkout</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <i className="ri-truck-line text-accent-green text-lg"></i>
                    <span>Fast shipping</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <i className="ri-customer-service-2-line text-accent-green text-lg"></i>
                    <span>24/7 support</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="border-t border-gray-200 pt-8">
              <button
                onClick={() => setShowReviews(!showReviews)}
                className="flex items-center justify-between w-full mb-6 cursor-pointer"
              >
                <h3 className="text-2xl font-bold text-gray-900">
                  Customer Reviews
                </h3>
                <i className={`ri-arrow-${showReviews ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              
              {showReviews && (
                <ProductReviews productId={product.id} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}